/**
 * CompactPipelineStatus - Compact status line for case lists
 * Shows: 🔍 Classifier AI working (89/127) → 🕵️ Anomaly AI next → 👤 8 items review
 */

import React from 'react';
import { ClaimPipeline, AgentStatus, AI_AGENTS } from './types';
import { Loader2, ArrowRight, User, CheckCircle, AlertCircle } from 'lucide-react';

interface CompactPipelineStatusProps {
  pipeline?: ClaimPipeline | null;
  loading?: boolean;
}

export default function CompactPipelineStatus({ pipeline, loading }: CompactPipelineStatusProps) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-400">
        <Loader2 className="w-3 h-3 animate-spin" />
        Loading...
      </div>
    );
  }
  
  if (!pipeline || !pipeline.agents || pipeline.agents.length === 0) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-400">
        <span>⏸️</span> No active processing
      </div>
    );
  }
  
  const runningAgent = pipeline.agents.find(a => a.status === 'running');
  const waitingHuman = pipeline.agents.find(a => a.status === 'waiting_human');
  const nextQueued = pipeline.agents.find(a => a.status === 'queued');
  const allComplete = pipeline.agents.every(a => a.status === 'complete' || a.status === 'idle');
  
  // All complete state
  if (allComplete && !runningAgent && !waitingHuman) {
    return (
      <div className="flex items-center gap-2 text-xs text-emerald-600">
        <CheckCircle className="w-3 h-3" />
        <span>AI analysis complete</span>
      </div>
    );
  }
  
  return (
    <div className="flex items-center gap-2 flex-wrap text-xs">
      {/* Currently running agent */}
      {runningAgent && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full">
          <span>{AI_AGENTS[runningAgent.agentId]?.icon || '🤖'}</span>
          <span className="font-medium">{runningAgent.agentName.replace(' AI', '')}</span>
          {runningAgent.progress && (
            <span className="text-blue-500">
              ({runningAgent.progress.current}/{runningAgent.progress.total})
            </span>
          )}
          <Loader2 className="w-3 h-3 animate-spin" />
        </span>
      )}
      
      {/* Next agent */}
      {nextQueued && runningAgent && (
        <>
          <ArrowRight className="w-3 h-3 text-slate-400" />
          <span className="inline-flex items-center gap-1 text-slate-500">
            <span>{AI_AGENTS[nextQueued.agentId]?.icon || '🤖'}</span>
            <span>{nextQueued.agentName.replace(' AI', '')} next</span>
          </span>
        </>
      )}
      
      {/* Human review needed */}
      {(pipeline.pendingHumanReview > 0 || waitingHuman) && (
        <>
          {(runningAgent || nextQueued) && <ArrowRight className="w-3 h-3 text-slate-400" />}
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full">
            <User className="w-3 h-3" />
            <span>{pipeline.pendingHumanReview || 1} need review</span>
          </span>
        </>
      )}
      
      {/* Error state */}
      {pipeline.agents.some(a => a.status === 'error') && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-700 rounded-full">
          <AlertCircle className="w-3 h-3" />
          <span>Error</span>
        </span>
      )}
    </div>
  );
}
